import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from "@angular/forms";
import {
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  ElementRef
} from "@angular/core";
import { NgxSpinnerService } from "ngx-spinner";
import {
  CheckDuplicateMsd,
  MotorDataDataService
} from "src/app/share/data-service/motor.data.service";
import { AlertService } from "src/app/share/service/alert.service";
import { MatStepper } from "@angular/material/stepper";
import { MotorRequestInterface } from "src/app/share/interface/motor.interface";
import { filter, Subscription } from "rxjs";
import { MasterDataDataService } from "src/app/share/data-service/master-data.data.service";
import { FileService } from "src/app/share/service/file.service";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { ValidatorFunction } from "src/app/share/function/validator.function";
import { PartnerDataService } from "src/app/share/service/partnerData.service";
import { PartnerService } from "src/app/share/service/partner.service";
import { MatDialog } from "@angular/material/dialog";
import { DuplicateMsdDialogComponent } from "../../components/duplicate-msd-dialog/duplicate-msd-dialog.component";

@Component({
  selector: "app-customer-form",
  templateUrl: "./customer-form.component.html",
  styleUrls: ["./customer-form.component.scss"]
})
export class CustomerFormComponent implements OnInit, OnDestroy {
  @ViewChild("stepper") stepper: MatStepper;
  @ViewChild("fileInput") fileInputRef: ElementRef;

  id: string;
  partnerCode: string;

  memberFormGroup: FormGroup;
  renewalFormGroup: FormGroup;
  docFormGroup: FormGroup;
  termsFormGroup: FormGroup;

  months: number[] = Array.from({ length: 4 }, (_, i) => (i + 1) * 3);

  payslip: File[] = [];
  acceptFile: string = ".pdf,.png,.jpg,.jpeg";
  maxLength: number = 3; // max files
  fileSize: number = 10; // MB
  fileList: File[] = [];

  docRequirements: number[] = Array.from({ length: 4 }, (_, i) => i + 1);
  terms: number[] = Array.from({ length: 6 }, (_, i) => i + 1);

  stateList: string[] = [
    "Selangor",
    "Wilayah Persekutuan Kuala Lumpur",
    "Wilayah Persekutuan Labuan",
    "Wilayah Persekutuan Putrajaya",
    "Johor",
    "Kedah",
    "Kelantan",
    "Melaka",
    "Negeri Sembilan",
    "Pahang",
    "Perak",
    "Perlis",
    "Pulau Pinang",
    "Sarawak",
    "Sabah",
    "Terengganu"
  ];

  subscriptions: Subscription[] = [];
  motorPartnerMemberList: string[] = ["prolindung"];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private ngxSpinnerService: NgxSpinnerService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private fileService: FileService,
    private partnerService: PartnerService,
    private partnerDataService: PartnerDataService,
    private motorDataDataService: MotorDataDataService,
    private masterDataDataService: MasterDataDataService
  ) {
    this.id = this.activatedRoute.snapshot.params.id;

    this.createForm();
    this.listenChanges();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((_event: any) => {
        const currentUrl = this.router.url;
        const currentParam = this.getQueryParamsFromUrl(currentUrl);

        sessionStorage.setItem("lastQueryParams", JSON.stringify(currentParam));
      });
  }

  async ngOnInit(): Promise<void> {
    await this.initPartnerDetail();

    await Promise.all([this.getDetailById(), this.getPolicyDetailById()]);

    this.getAllPostcodes();

    this.onPreventBack();
  }

  private getMotorPartnerMemberByIdentityNo(identityNo: string) {
    this.motorDataDataService.getMotorPartnerMemberByIdentityNo(identityNo).subscribe({
      next: (value) => {
        this.memberFormGroup.get("staffNo").patchValue(value.staffNo);
        this.memberFormGroup.controls.staffNo.disable();
        this.memberFormGroup.get("memberNo").patchValue(value.memberId);
        this.memberFormGroup.controls.memberNo.disable();
      },
      error: () => {
      }
    });
  }

  async initPartnerDetail(): Promise<void> {
    this.partnerCode = await this.partnerService.getPartnerCode();
    this.partnerDataService.getPartnerDetail(this.partnerCode);
  }

  private getAllPostcodes(): void {
    this.masterDataDataService.selectAllPostcodes().subscribe({
      next: (value) => {
        ["postcode", "officePostcode"].forEach((key) => {
          this.memberFormGroup
            .get(key)
            .addValidators((control: FormControl) =>
              ValidatorFunction.postcodeValidator(control, value)
            );
        });
      },
      error: () => {
        this.alertService.openSnackBar("Gagal Mendapatkan Poskod");
      }
    });
  }

  private async getPolicyDetailById(): Promise<void> {
    this.motorDataDataService.getPolicyInfoById(this.id).subscribe({
      next: (value: MotorRequestInterface) => {
        this.memberFormGroup.patchValue(value);
        this.renewalFormGroup.get("totalAmt").patchValue(value.total);

        this.onCalculateStampDuty();
        this.onCalculateMonthlyAmt(
          this.renewalFormGroup.get("instalmentMonths").value,
          true
        );
      },
      error: () => {
        window.location.href = "/";
      }
    });
  }

  private async getDetailById(): Promise<void> {
    await this.motorDataDataService.getDetailByPolicyId(this.id).subscribe({
      next: (value) => {
        this.checkMsdDetail(value.id);

        this.memberFormGroup.patchValue(value);
        this.renewalFormGroup.patchValue(value);

        if (this.motorPartnerMemberList.includes(this.partnerCode)) 
          this.getMotorPartnerMemberByIdentityNo(this.memberFormGroup.get("identityNo").value);

      },
      error: () => {
        window.location.href = "/";
      }
    });
  }

  checkMsdDetail(id: string): void {
    try {
      this.motorDataDataService.checkMsdRequestInfoByRequestId(id).subscribe({
        next: (value: CheckDuplicateMsd) => {
          if (value.isExist === "N") return;

          this.dialog.open(DuplicateMsdDialogComponent, {
            data: this.id,
            autoFocus: false,
            disableClose: true
          });
        },
        error: () => {}
      });
    } catch (error) {}
  }

  private createForm(): void {
    this.memberFormGroup = this.formBuilder.group({
      name: [null, Validators.required],
      identityNo: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      staffNo: [null, null],
      mobileNo: [null, Validators.required],
      memberNo: [null, null],
      officeTelNo: [null, ValidatorFunction.mobileNoValidator],
      officeTelExt: [null, ValidatorFunction.mobileExtValidator],
      officeAddressLineOne: [null, Validators.required],
      officeAddressLineTwo: [null, Validators.required],
      officeCity: [null, Validators.required],
      officePostcode: [null, Validators.required],
      officeState: [null, Validators.required],

      address: [null, Validators.required],
      address2: [null, Validators.required],
      city: [null, Validators.required],
      postcode: [null, Validators.required],
      state: [null, Validators.required]
    });

    this.renewalFormGroup = this.formBuilder.group({
      vehicleNo: [{ disabled: true, value: null }],
      roadtaxExpiryDate: [null, Validators.required],
      totalAmt: [{ disabled: true, value: null }],
      stampDuty: [{ disabled: true, value: null }],
      insuranceRoadtaxRenewalApplicationAmt: [{ disabled: true, value: null }],
      monthlyInstalmentAmt: [{ disabled: true, value: null }],
      instalmentMonths: [3]
    });

    this.docFormGroup = this.formBuilder.group({
      payslip: [
        null,
        [Validators.required, ValidatorFunction.minFilesValidator(1)]
      ]
    });

    this.termsFormGroup = this.formBuilder.group({
      signature: [null, Validators.required],
      dateSigned: [{ disabled: true, value: new Date() }]
    });
  }

  listenChanges(): void {
    this.subscriptions.push(
      this.renewalFormGroup
        .get("instalmentMonths")
        .valueChanges.subscribe((value) => {
          if (!value) return;

          this.onCalculateMonthlyAmt(value, false);
        }),

      this.memberFormGroup
        .get("officeTelExt")
        .valueChanges.subscribe((value) => {
          const officeTelNo = this.memberFormGroup.get("officeTelNo");

          if (value) {
            officeTelNo.addValidators(Validators.required);
          } else {
            officeTelNo.removeValidators(Validators.required);
          }

          officeTelNo.updateValueAndValidity();
        })
    );
  }

  onStepChange(event: StepperSelectionEvent, stepper: MatStepper) {
    if (event.selectedIndex === 3 && this.payslip.length < 3) {
      stepper.selectedIndex = event.previouslySelectedIndex; // stay on current step
    }
  }

  onCalculateStampDuty(): void {
    const totalAmt = this.renewalFormGroup.get("totalAmt").value;

    let stampDuty: number = 0;

    if (totalAmt <= 0) {
      stampDuty = 0;
    } else if (totalAmt <= 2000) {
      stampDuty = 10;
    } else {
      const increment = Math.ceil(totalAmt / 1000) - 1;
      const chargeAmt = 5 + (increment * 5);

      stampDuty = Math.min(chargeAmt, 35);
    }

    this.renewalFormGroup.get("stampDuty").patchValue(stampDuty);
  }

  onCalculateMonthlyAmt(month: number, isCalcTotal: boolean): void {
    const finalAmt: number =
      this.renewalFormGroup.get("totalAmt").value +
      this.renewalFormGroup.get("stampDuty").value;

    const monthlyAmt: number = finalAmt / month;
    const roundedAmt: number = Math.round(monthlyAmt * 100) / 100;

    this.renewalFormGroup.get("monthlyInstalmentAmt").patchValue(roundedAmt);

    if (!isCalcTotal) return;
    this.renewalFormGroup
      .get("insuranceRoadtaxRenewalApplicationAmt")
      .patchValue(finalAmt);
  }

  onTriggerFile(): void {
    this.fileInputRef.nativeElement.click();
  }

  deleteFile(index: number): void {
    this.payslip.splice(index, 1);
    this.fileList.splice(index, 1);

    this.docFormGroup.get("payslip")?.setValue(this.payslip);
    this.docFormGroup.get("payslip")?.updateValueAndValidity();
  }

  async handleFileInput(e: any): Promise<void> {
    const files = (e.target as HTMLInputElement).files;
    const fileType = (e.target as HTMLInputElement).name;

    if (!files || files.length === 0) return;

    for (let file of Array.from(files)) {
      const isUploadedFilesValid = await this.fileService.isUploadedFilesValidNoMax({
        file: file,
        files: files,
        currLength: this.fileList.length,
        maxLength: this.maxLength,
        accept: this.acceptFile,
        name: fileType,
        size: this.fileSize
      });

      if (!isUploadedFilesValid) {
        e.target.value = null;
        continue;
      }

      if (!this[fileType]) {
        this[fileType] = [];
      }

      this[fileType].push(file);
      this.fileList.push(file);
    }

    this.docFormGroup.get(fileType)?.setValue(this[fileType]);
    this.docFormGroup.get(fileType)?.updateValueAndValidity();

    e.target.value = null;
  }

  onSubmit(): void {
    this.ngxSpinnerService.show();

    this.motorDataDataService
      .submitCustomerForm(this.id, this.buildCustomerFormData())
      .subscribe({
        next: (x) => {
          if (x.isSuccess) {
            sessionStorage.removeItem("lastQueryParams");
            window.location.href =
              "/customer-form/success/" + this.id + "?partnerCode=prolindung";
          } else {
            this.alertService.openSnackBar("Gagal Menghantar Borang");
            this.ngxSpinnerService.hide();
          }
        },
        error: (err) => {
          this.ngxSpinnerService.hide();

          if (
            err.error === "Permohonan anda telah dihantar dan sedang disemak."
          ) {
            this.dialog.open(DuplicateMsdDialogComponent, {
              data: this.id,
              autoFocus: false,
              disableClose: true
            });
            return;
          }

          this.alertService.openSnackBar(err.error, null, 5000);
        }
      });
  }

  private buildCustomerFormData(): FormData {
    const formData: any = new FormData();

    // Loop Step 1 Form
    for (const [key, value] of Object.entries(
      this.memberFormGroup.getRawValue()
    )) {
      if (value === null || value == undefined || value === "") continue;

      formData.append(key, value);
    }

    // Loop Step 2 Form
    for (const [key, value] of Object.entries(
      this.renewalFormGroup.getRawValue()
    ) as [string, any][]) {
      if (value === null || value == undefined || value === "") continue;

      if (key === "roadtaxExpiryDate") {
        formData.append(key, new Date(value).toISOString());
      } else {
        formData.append(key, value);
      }
    }

    // Loop Step 3 Form
    for (const [key, value] of Object.entries(
      this.docFormGroup.getRawValue()
    ) as [string, any][]) {
      if (value === null || value == undefined || value === "") continue;

      value.forEach((file: File) => {
        formData.append(key, file, file.name);
      });
    }

    // Loop Step 4 Form
    for (const [key, value] of Object.entries(
      this.termsFormGroup.getRawValue()
    ) as [string, any][]) {
      if (key === "dateSigned") {
        formData.append(key, new Date(value).toISOString());
      } else {
        formData.append(key, value);
      }
    }

    return formData;
  }

  private getQueryParamsFromUrl(url: string): any {
    const param = new URLSearchParams(url.split("?")[1]);
    const result: any = {};

    param.forEach((value, key) => (result[key] = value));
    return result;
  }

  onPreventBack(): void {
    history.pushState(null, "", window.location.href);

    window.addEventListener("popstate", () => {
      history.pushState(null, "", window.location.href);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }
}
